import { useEffect, useState } from 'react';

import { browserLogger } from '@widget/utils/datadog';
import {
  Widget as WidgetCore,
  useLeadData,
  useAgentContext,
  useFlags,
  useZipCodeLocation,
  useMarketTrends,
} from '@widget/core';
import { ValuationProvider } from '@widget/valuation';
import { WidgetLayout } from '../presets/WidgetLayout';
import { prepareLeadData } from './prepareLeadData';
import { StepSlideTransition } from '../transitions';
import { sendLeadData, setUserTimestamp, updateLeadData } from '../api';
import { renderStep } from './renderStep';
import { useWidgetQuery } from './useWidgetQuery';
import '../types/extendWindow';
import { WidgetProviders } from './WidgetProviders';
import { getFlagsFromResponse } from './utils';
import { getTimeSlots } from '@widget/api/timeSlots';
import { useTimeslots } from '@widget/core/TimeSlotsProvider';
import { services } from '@widget/config/redirects';
import { prepareLeadUpdateData } from '@widget/storyblok/prepareLeadUpdateData';

import type { AppointmentType, TimeSlotsObjectType, TimeSlotsType } from '@widget/api/types';
import type { WidgetItemSchema } from './schemas';
import type { ReactFCWithChildren } from '@widget/types/ReactFCWithChildren';
import type { WidgetProps as WidgetCoreProps } from '@widget/core';
import type { Richtext } from 'storyblok-js-client';

export type WidgetProps = Pick<WidgetCoreProps, 'onClose' | 'initialState'> & {
  id: string;
  market: string;
  brokerEmail?: string;
  preloaded?: boolean;
  apiToken: string;
  apiVersion?: 'draft' | 'published';
  widgetItemResponse?: ReturnType<typeof WidgetItemSchema.parse>;
  widgetXBaseApi?: string;
  transactionIdParent?: string;
  showCloseIcon?: boolean;
};

export const WidgetComponent = ({
  id,
  onClose,
  initialState,
  widgetItemResponse,
  transactionIdParent,
  showCloseIcon = false,
  brokerEmail,
}: WidgetProps): JSX.Element => {
  const [valuation, setValuation] = useState<string | null>(null);
  const { setLeadData, leadData, setLoadingLead, setLeadError } = useLeadData();
  const { setTimeslots, setTimeslotsError } = useTimeslots();
  const flags = useFlags();

  const { agent, loadAgentInfo } = useAgentContext();
  const { fetchZipcodeLocationData } = useZipCodeLocation();
  const { fetchMarketTrends } = useMarketTrends();

  const fetchDataByZip = (zipCode: unknown) => {
    if (zipCode && typeof zipCode === 'string') {
      flags.personalization && loadAgentInfo(zipCode);
      fetchZipcodeLocationData(zipCode);
      fetchMarketTrends(zipCode);
    }
  };

  useEffect(() => {
    setUserTimestamp();
  }, []);

  useEffect(() => {
    if ((initialState?.history?.length || 0) > 0) {
      const zipCode = initialState?.history?.reduce(
        (acc, step) => ({ ...acc, ...step.formData }),
        {} as Record<string, unknown>,
      ).zipCode;
      fetchDataByZip(zipCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialState]);

  const handleCommit = (formData: Record<string, unknown>) => {
    const { zipCode } = formData;
    fetchDataByZip(zipCode);
  };

  // @TODO: handle validation errors
  async function handleSubmitLead(formData: Record<string, unknown>): Promise<void> {
    try {
      const leadData = await prepareLeadData(formData, valuation);
      const response = await sendLeadData(leadData, {
        widgetId: id,
        flags,
        agent: agent?.name || '',
      });
      setLeadData(
        response?.data
          ? {
              ...response?.data,
              isNewAppointment: response?.data.bookedSlot?.isNewAppointment,
            }
          : null,
      );
    } catch (err) {
      browserLogger.error(err, 'widget send lead data');
      setLeadError({ error: true });
    } finally {
      setLoadingLead(false);
    }
  }

  async function handleUpdateLeadData(formData: Record<string, unknown>): Promise<void> {
    if (leadData?.id === undefined) {
      return;
    }

    const sanitizedFormData = prepareLeadUpdateData(formData);
    const response = await updateLeadData(leadData.id, sanitizedFormData);
    if (response?.error) {
      setLeadError(response?.errorData || null);
    }
  }

  const handleSetTimeslots = (
    appointmentType: AppointmentType,
    data: TimeSlotsObjectType | null,
  ) => {
    setTimeslots(
      (prevState: TimeSlotsType | null) =>
        ({
          ...(prevState || {}),
          [appointmentType]: data,
        } as TimeSlotsType),
    );
  };

  async function handleGetTimeSlots({
    leadId,
    zipCode,
    appointmentType,
    timezone,
  }: {
    leadId?: string;
    appointmentType: AppointmentType;
    timezone: string;
    zipCode?: string;
  }): Promise<void> {
    try {
      const response = await getTimeSlots({
        leadId,
        appointmentType,
        timezone,
        zipCode,
        brokerEmail,
      });
      if (!response.data) {
        handleSetTimeslots(appointmentType, null);
        return;
      }
      handleSetTimeslots(appointmentType, response.data);
    } catch (err) {
      setTimeslotsError('timeslots error');
      browserLogger.error(err, 'timeslots fetch error - widget');
    }
  }

  return (
    <WidgetCore
      initialState={initialState}
      onClose={onClose}
      enableReset={widgetItemResponse?.content?.enableReset || false}
      cancelIcon={widgetItemResponse?.content?.cancelIcon}
      onCommit={handleCommit}
      onSubmitLead={handleSubmitLead}
      onUpdateLead={handleUpdateLeadData}
      onRequestTimeSlots={handleGetTimeSlots}
      widgetId={id}
      leadData={leadData}
      analyticsMap={widgetItemResponse?.content?.analyticsMap}
      transactionIdParent={transactionIdParent}
      maxNumberOfSteps={Number(widgetItemResponse?.content?.maxNumberOfSteps || 0)}
      showCloseIcon={showCloseIcon}
      render={({ children }) => (
        <ValuationProvider onValuationChange={setValuation}>
          <WidgetLayout
            onClickLogo={onClose}
            closeButtonText={widgetItemResponse?.content?.closeButtonText}
            logo={widgetItemResponse?.content?.logo}
            contactPerson={widgetItemResponse?.content?.contactPerson}
            localizedUspLabel={widgetItemResponse?.content?.localizedUspLabel}
            closeModalTitle={widgetItemResponse?.content?.closeModalTitle}
            closeModalSubtitle={widgetItemResponse?.content?.closeModalSubtitle}
            closeModalCloseModalText={widgetItemResponse?.content?.closeModalCloseModalText}
            closeModalCloseWidgetText={widgetItemResponse?.content?.closeModalCloseWidgetText}
            marketingHeaderText={
              widgetItemResponse?.content?.marketingHeaderText as Richtext | string
            }
            trustIconsList={widgetItemResponse?.content?.trustIconsList}
            footerItems={widgetItemResponse?.content?.footer}
            showCloseIcon={showCloseIcon}
          >
            <StepSlideTransition>{children}</StepSlideTransition>
          </WidgetLayout>
        </ValuationProvider>
      )}
    >
      {widgetItemResponse?.content?.steps?.map(renderStep) ?? null}
    </WidgetCore>
  );
};

export const Widget: ReactFCWithChildren<WidgetProps> = (props) => {
  const { data } = useWidgetQuery({
    id: props.id,
    apiToken: props.apiToken,
    apiVersion: props.apiVersion || 'published',
    skipQuery: props.preloaded,
  });
  const response = props.preloaded ? props.widgetItemResponse : data?.WidgetItem;

  if (!response) {
    return <></>;
  }

  const flags = getFlagsFromResponse(response);
  const trustIcons = response.content?.trustIconsList;
  const uspTextItems = response.content?.footer;
  const localizedUspLabel = response.content?.localizedUspLabel;

  return (
    <WidgetProviders
      market={props.market}
      flags={flags}
      trustIcons={trustIcons || []}
      uspTextItems={uspTextItems}
      localizedUspLabel={localizedUspLabel}
      widgetXBaseApi={props.widgetXBaseApi ?? services.widgetApp}
    >
      <WidgetComponent {...props} widgetItemResponse={response} />
    </WidgetProviders>
  );
};
